
import { useState } from 'react';

type InterestType = 'producto' | 'agente' | '';

export default function ContactForm() {
  const [interest, setInterest] = useState<InterestType>('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!interest) return;

    const form = e.currentTarget;
    const data = new URLSearchParams();
    data.append('nombre', (form.elements.namedItem('nombre') as HTMLInputElement).value);
    data.append('email', (form.elements.namedItem('email') as HTMLInputElement).value);
    data.append('telefono', (form.elements.namedItem('telefono') as HTMLInputElement).value);
    data.append('interes', interest === 'producto' ? 'Información sobre productos de protección familiar' : 'Información sobre el programa de Agente Financiero');
    const msgVal = (form.elements.namedItem('mensaje') as HTMLTextAreaElement).value;
    if (msgVal.length > 500) return;
    data.append('mensaje', msgVal);

    setLoading(true);
    try {
      await fetch('https://readdy.ai/api/form/d6j0tb1bfjp31emakdt0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setMessage('Hubo un error. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-[#161616] border border-[#E8A045]/30 rounded-2xl p-10 md:p-14 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-[#E8A045]/10 rounded-full mx-auto mb-6 border border-[#E8A045]/30">
          <i className="ri-check-double-line text-[#E8A045] text-3xl"></i>
        </div>
        <h3 className="text-2xl font-black text-white mb-3">¡Mensaje enviado!</h3>
        <p className="text-white/50 text-base leading-relaxed max-w-sm mx-auto">
          Gracias por contactarnos. Un agente se comunicará contigo muy pronto.
        </p>
      </div>
    );
  }

  return (
    <form
      id="contact-form-home"
      data-readdy-form
      onSubmit={handleSubmit}
      className="bg-[#161616] border border-white/8 rounded-2xl p-7 md:p-10 space-y-6"
    >
      {/* Selector de interés */}
      <div>
        <label className="block text-white/70 text-sm font-medium mb-3">
          ¿Qué información te interesa? <span className="text-[#E8A045]">*</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setInterest('producto')}
            className={`cursor-pointer rounded-xl border p-4 text-left transition-all duration-300 ${
              interest === 'producto'
                ? 'border-[#E8A045] bg-[#E8A045]/10'
                : 'border-white/10 bg-white/3 hover:border-white/25'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5 ${interest === 'producto' ? 'bg-[#E8A045]/20' : 'bg-white/5'}`}>
                <i className={`ri-shield-heart-fill text-lg ${interest === 'producto' ? 'text-[#E8A045]' : 'text-white/40'}`}></i>
              </div>
              <div>
                <p className={`font-semibold text-sm ${interest === 'producto' ? 'text-white' : 'text-white/60'}`}>
                  Protección Familiar
                </p>
                <p className="text-white/35 text-xs mt-0.5 leading-relaxed">
                  Seguros, retiro y planes de protección
                </p>
              </div>
            </div>
            {interest === 'producto' && (
              <div className="flex justify-end mt-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-checkbox-circle-fill text-[#E8A045] text-lg"></i>
                </div>
              </div>
            )}
          </button>

          <button
            type="button"
            onClick={() => setInterest('agente')}
            className={`cursor-pointer rounded-xl border p-4 text-left transition-all duration-300 ${
              interest === 'agente'
                ? 'border-[#E8A045] bg-[#E8A045]/10'
                : 'border-white/10 bg-white/3 hover:border-white/25'
            }`}
          >
            <div className="flex items-start gap-3">
              <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5 ${interest === 'agente' ? 'bg-[#E8A045]/20' : 'bg-white/5'}`}>
                <i className={`ri-briefcase-fill text-lg ${interest === 'agente' ? 'text-[#E8A045]' : 'text-white/40'}`}></i>
              </div>
              <div>
                <p className={`font-semibold text-sm ${interest === 'agente' ? 'text-white' : 'text-white/60'}`}>
                  Programa de Agente
                </p>
                <p className="text-white/35 text-xs mt-0.5 leading-relaxed">
                  Carrera como Agente Financiero
                </p>
              </div>
            </div>
            {interest === 'agente' && (
              <div className="flex justify-end mt-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-checkbox-circle-fill text-[#E8A045] text-lg"></i>
                </div>
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Nombre */}
      <div>
        <label htmlFor="nombre" className="block text-white/70 text-sm font-medium mb-2">
          Nombre completo <span className="text-[#E8A045]">*</span>
        </label>
        <input
          id="nombre"
          name="nombre"
          type="text"
          required
          placeholder="Tu nombre completo"
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#E8A045]/60 focus:bg-white/8 transition-all duration-200"
        />
      </div>

      {/* Email y Teléfono */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="email" className="block text-white/70 text-sm font-medium mb-2">
            Correo electrónico <span className="text-[#E8A045]">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            placeholder="tu@correo.com"
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#E8A045]/60 focus:bg-white/8 transition-all duration-200"
          />
        </div>
        <div>
          <label htmlFor="telefono" className="block text-white/70 text-sm font-medium mb-2">
            Teléfono
          </label>
          <input
            id="telefono"
            name="telefono"
            type="tel"
            placeholder="(555) 000-0000"
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#E8A045]/60 focus:bg-white/8 transition-all duration-200"
          />
        </div>
      </div>

      {/* Mensaje */}
      <div>
        <label htmlFor="mensaje" className="block text-white/70 text-sm font-medium mb-2">
          Mensaje <span className="text-white/30 font-normal">(opcional)</span>
        </label>
        <textarea
          id="mensaje"
          name="mensaje"
          rows={4}
          maxLength={500}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Cuéntanos un poco más sobre lo que buscas..."
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#E8A045]/60 focus:bg-white/8 transition-all duration-200 resize-none"
        />
        <p className="text-white/25 text-xs mt-1 text-right">{message.length}/500</p>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={!interest || loading}
        className={`w-full py-4 rounded-xl font-bold text-sm transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer ${
          interest && !loading
            ? 'bg-[#E8A045] hover:bg-[#d4913a] text-[#0D0D0D] shadow-lg shadow-[#E8A045]/20 hover:shadow-[#E8A045]/40 hover:-translate-y-0.5 transform'
            : 'bg-white/10 text-white/30 cursor-not-allowed'
        }`}
      >
        {loading ? (
          <>
            <i className="ri-loader-4-line animate-spin text-lg"></i>
            <span>Enviando...</span>
          </>
        ) : (
          <>
            <i className="ri-send-plane-fill text-lg"></i>
            <span>Enviar mensaje</span>
          </>
        )}
      </button>

      <p className="text-white/25 text-xs text-center">
        Tu información es privada y nunca será compartida con terceros.
      </p>
    </form>
  );
}
